import React, { createContext, useContext, useState, useEffect } from "react";
import { H } from "highlight.run";

interface User {
  id: string;
  name: string;
  email: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const token = localStorage.getItem("login_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));

        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem("login_token");
          setUser(null);
        } else {
          setUser({
            id: payload.sub,
            name: payload.name || "",
            email: payload.email || "",
            picture: payload.picture || "",
          });

          H.identify(payload.email);
        }
      } catch (error) {
        console.error("Error parsing auth token", error);
        localStorage.removeItem("login_token");
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const login = (token: string) => {
    localStorage.setItem("login_token", token);
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      setUser({
        id: payload.sub,
        name: payload.name || "",
        email: payload.email || "",
        picture: payload.picture || "",
      });

      H.identify(payload.email);
    } catch (error) {
      console.error("Error parsing auth token during login", error);
    }
  };

  const logout = () => {
    localStorage.removeItem("login_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
